import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const SafetyManager = ({ initialTab = 'incidents', initialSelectedId = null }) => {
    // --- TABS & PERMISSIONS ---
    const [activeTab, setActiveTab] = useState(initialTab); 
    const [isOwner, setIsOwner] = useState(false); 
    const [auditToEdit, setAuditToEdit] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768); 

    useEffect(() => {
        if (initialTab) setActiveTab(initialTab);
    }, [initialTab]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);

        const checkRole = async () => {
            try {
                const res = await api.get('users/me/');
                setIsOwner(res.data.is_superuser || res.data.is_staff);
            } catch (e) { console.error(e); }
        };
        checkRole();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleEditAudit = (audit) => {
        setAuditToEdit(audit);
        setActiveTab('new_audit');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleAuditSuccess = () => {
        setAuditToEdit(null);
        setActiveTab('audits');
    };

    const getTabStyle = (isActive, color) => ({
        padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600,
        background: isActive ? color : 'transparent', color: isActive ? 'white' : THEME.textSec, transition: 'all 0.2s'
    });

    return (
        <div className="animate-fade-in">
            {/* HEADER */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', borderBottom:`1px solid ${THEME.border}`, paddingBottom:'15px', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '10px' : '0'}}>
                <div>
                    <h2 style={{color: 'white', margin:0}}>🦺 Safety Management Hub</h2>
                    <p style={{color: THEME.textSec, margin:'5px 0 0 0', fontSize:'0.9rem'}}>Incident reporting and site safety.</p>
                </div>
                
                <div style={{display:'flex', gap:'5px', background: THEME.card, padding:'4px', borderRadius:'8px', border:`1px solid ${THEME.border}`, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start'}}>
                    <button onClick={() => setActiveTab('new_report')} style={getTabStyle(activeTab === 'new_report', THEME.danger)}>📝 File Report</button>
                    <button onClick={() => setActiveTab('incidents')} style={getTabStyle(activeTab === 'incidents', THEME.danger)}>🔍 Database</button>
                    
                    {isOwner && (
                        <>
                            <div style={{width:'1px', background: THEME.border, margin:'0 5px'}}></div>
                            <button onClick={() => { setActiveTab('new_audit'); setAuditToEdit(null); }} style={getTabStyle(activeTab === 'new_audit', THEME.primary)}>
                                {auditToEdit ? '🖊️ Edit' : '📋 Conduct'}
                            </button>
                            <button onClick={() => setActiveTab('audits')} style={getTabStyle(activeTab === 'audits', THEME.primary)}>📚 History</button>
                            <button onClick={() => setActiveTab('builder')} style={getTabStyle(activeTab === 'builder', '#a855f7')}>🛠️ Templates</button>
                        </>
                    )}
                </div>
            </div>

            {/* CONTENT AREA */}
            <div className="card" style={{minHeight:'500px', borderTop: activeTab.includes('audit') ? `4px solid ${THEME.primary}` : activeTab === 'builder' ? '4px solid #a855f7' : `4px solid ${THEME.danger}`, padding:0}}>
                <div style={{padding: isMobile ? '10px' : '20px'}}>
                    {activeTab === 'new_report' && <FileReportForm onSuccess={() => setActiveTab('incidents')} isMobile={isMobile} />}
                    {activeTab === 'incidents' && <IncidentDatabase isMobile={isMobile} initialExpandedId={initialTab === 'incidents' ? initialSelectedId : null} />}
                    {isOwner && activeTab === 'new_audit' && <AuditForm initialData={auditToEdit} onSuccess={handleAuditSuccess} onCancel={() => { setAuditToEdit(null); setActiveTab('audits'); }} isMobile={isMobile} />}
                    {isOwner && activeTab === 'audits' && <AuditHistory onEdit={handleEditAudit} isMobile={isMobile} initialExpandedId={initialTab === 'audits' ? initialSelectedId : null} />}
                    {isOwner && activeTab === 'builder' && <AuditBuilder isMobile={isMobile} />}
                </div>
            </div>
        </div>
    );
};

const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#0f172a', color:'white', boxSizing: 'border-box', marginBottom:'10px' };

// --- HELPER TO RENDER ACTION LINKS ---
const LinkedActionsList = ({ actions }) => {
    if (!actions || actions.length === 0) return null;
    return (
        <div style={{marginTop:'15px', paddingTop:'15px', borderTop:`1px dashed ${THEME.border}`}}>
            <h5 style={{margin:'0 0 10px 0', color:'#cbd5e1', fontSize:'0.85rem'}}>🔗 Linked Corrective Actions</h5>
            <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                {actions.map(act => (
                    <div key={act.id} style={{display:'flex', alignItems:'center', gap:'10px', background:'rgba(255,255,255,0.03)', padding:'8px', borderRadius:'6px', border:`1px solid ${THEME.border}`}}>
                        {/* UPDATED: Use Display ID */}
                        <span style={{color:THEME.warning, fontWeight:'bold', fontSize:'0.85rem'}}>{act.display_id || `#${act.id}`}</span>
                        <span style={{color:'white', flex:1, fontSize:'0.9rem'}}>{act.title}</span>
                        <span style={{fontSize:'0.75rem', padding:'2px 8px', borderRadius:'4px', background: act.status==='OPEN' ? `${THEME.danger}22` : `${THEME.success}22`, color: act.status==='OPEN' ? THEME.danger : THEME.success, border:`1px solid ${act.status==='OPEN' ? THEME.danger : THEME.success}44`}}>
                            {act.status}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ==========================================
// 1. INCIDENT DATABASE
// ==========================================
const IncidentDatabase = ({ isMobile, initialExpandedId }) => {
    const [reports, setReports] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [search, setSearch] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [note, setNote] = useState('');

    useEffect(() => { 
        api.get('safety/').then(res => {
            setReports(res.data);
            if (initialExpandedId) {
                const found = res.data.find(r => r.id === initialExpandedId);
                if (found) {
                    setExpandedId(found.id);
                    setTimeout(() => {
                        const el = document.getElementById(`incident-row-${found.id}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 500);
                }
            }
        }); 
    }, [initialExpandedId]);

    const handleStatus = async (id, status) => { await api.patch(`safety/${id}/`, { status }); const res = await api.get('safety/'); setReports(res.data); };
    const sendNote = async (id) => { if(!note) return; await api.post(`safety/${id}/notes/`, { text: note }); setNote(''); const res = await api.get('safety/'); setReports(res.data); };

    const filtered = useMemo(() => {
        return reports.filter(r => {
            const matchesStatus = statusFilter === 'ALL' || r.status === statusFilter;
            const uniqueId = r.display_id || `INC-${String(r.id).padStart(3, '0')}`;
            
            const matchesSearch = !search || 
                uniqueId.toLowerCase().includes(search.toLowerCase()) || 
                r.description.toLowerCase().includes(search.toLowerCase()) || 
                (r.company_name || '').toLowerCase().includes(search.toLowerCase()) ||
                (r.reported_by_name || '').toLowerCase().includes(search.toLowerCase()) || // Added search by reporter
                r.location.toLowerCase().includes(search.toLowerCase());
            
            const matchesDate = !dateFilter || r.created_at.startsWith(dateFilter);
            return matchesStatus && matchesSearch && matchesDate;
        });
    }, [reports, statusFilter, search, dateFilter]);

    const handleExport = () => {
        if (filtered.length === 0) return alert("No data to export");
        const headers = ["ID", "Ref", "Type", "Severity", "Status", "Date", "Location", "Reported By", "Description"];
        const csvRows = [headers.join(',')];
        filtered.forEach(r => {
            const clean = (t) => `"${(t||'').toString().replace(/"/g, '""')}"`;
            csvRows.push([
                r.id,
                r.display_id || `INC-${r.id}`,
                r.report_type, r.severity, r.status, 
                new Date(r.created_at).toLocaleDateString(), 
                clean(r.location), clean(r.reported_by_name), clean(r.description)
            ].join(','));
        });
        const url = window.URL.createObjectURL(new Blob([csvRows.join('\n')], { type: 'text/csv' }));
        const link = document.createElement('a'); link.href = url; link.setAttribute('download', `Safety_Incidents_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link); link.click(); link.remove();
    };

    return (
        <div>
            <div style={{marginBottom:'20px', background:THEME.card, padding:'15px', borderRadius:'12px', border:`1px solid ${THEME.border}`}}>
                <div style={{display:'flex', gap:'10px', marginBottom:'15px', overflowX:'auto'}}>
                    {['ALL', 'OPEN', 'INVESTIGATING', 'CLOSED'].map(s => (
                        <button key={s} onClick={() => setStatusFilter(s)} style={{padding:'6px 12px', borderRadius:'4px', border:`1px solid ${statusFilter===s ? THEME.danger : THEME.border}`, cursor:'pointer', background: statusFilter===s ? THEME.danger : 'transparent', color: statusFilter===s ? 'white' : THEME.textSec, whiteSpace: 'nowrap', fontWeight:'bold'}}>
                            {s}
                        </button>
                    ))}
                </div>
                <div style={{display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center'}}>
                    <input type="text" placeholder="🔍 Search (ID, Reporter, Location)..." value={search} onChange={e => setSearch(e.target.value)} style={{...inputStyle, flex: 2, minWidth:'200px', marginBottom:0}} />
                    <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} style={{...inputStyle, width:'auto', marginBottom:0}} />
                    <button onClick={handleExport} style={{padding:'8px 16px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>📥 CSV</button>
                </div>
            </div>

            <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden'}}>
                <div style={{overflowX: 'auto'}}>
                    <table style={{width:'100%', minWidth: '800px', borderCollapse:'collapse', textAlign:'left', color: THEME.textSec, fontSize:'0.9rem'}}>
                        <thead style={{background:'#020617', borderBottom:`1px solid ${THEME.border}`}}>
                            <tr>
                                <th style={{padding:'15px'}}>ID</th>
                                <th>Type</th>
                                <th>Severity</th>
                                <th>Location</th>
                                <th>Company</th> 
                                <th>Reported By</th> {/* <--- NEW COLUMN */}
                                <th>Status</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => (
                                <React.Fragment key={r.id}>
                                    <tr id={`incident-row-${r.id}`} onClick={() => setExpandedId(expandedId === r.id ? null : r.id)} style={{borderBottom:`1px solid ${THEME.border}`, cursor:'pointer', background: expandedId===r.id ? 'rgba(239, 68, 68, 0.1)' : 'transparent'}}>
                                        <td style={{padding:'15px', color:'white', fontWeight:'bold'}}>{r.display_id || '#'+r.id}</td>
                                        <td>{r.report_type}</td>
                                        <td style={{color: r.severity==='CRITICAL' ? THEME.danger : r.severity==='HIGH' ? THEME.warning : THEME.primary}}>{r.severity}</td>
                                        <td>{r.location}</td>
                                        
                                        <td style={{color: THEME.warning, fontWeight:'bold'}}>{r.company_name || 'N/A'}</td>
                                        
                                        {/* --- NEW CELL --- */}
                                        <td style={{color: 'white'}}>{r.reported_by_name}</td>
                                        
                                        <td><span style={{padding:'2px 8px', borderRadius:'4px', background: r.status==='OPEN' ? THEME.danger : r.status==='CLOSED' ? THEME.success : THEME.warning, color:'white', fontSize:'0.75rem'}}>{r.status}</span></td>
                                        <td>{new Date(r.created_at).toLocaleDateString()}</td>
                                    </tr>
                                    {expandedId === r.id && (
                                        <tr style={{background:'#020617', borderBottom:`2px solid ${THEME.danger}`}}><td colSpan="8" style={{padding:'20px'}}> {/* Updated colSpan to 8 */}
                                            <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'20px'}}>
                                                <div>
                                                    <h4 style={{marginTop:0, color:'white'}}>Details</h4>
                                                    <p style={{color: '#e2e8f0', lineHeight: '1.6'}}>
                                                        {r.description}
                                                    </p>
                                                    <div style={{marginBottom:'15px', fontSize:'0.85rem', color:THEME.textSec}}>
                                                        <strong>Reported by:</strong> {r.reported_by_name} ({r.company_name})
                                                    </div>
                                                    
                                                    {/* Companies Involved Section */}
                                                    {r.companies_involved_names && r.companies_involved_names.length > 0 && (
                                                        <div style={{marginTop:'10px', marginBottom:'15px', display:'flex', flexWrap:'wrap', gap:'8px', alignItems:'center'}}>
                                                            <span style={{fontSize:'0.85rem', color:THEME.textSec}}>Companies Involved:</span>
                                                            {r.companies_involved_names.map((name, i) => (
                                                                <span key={i} style={{
                                                                    background: 'rgba(59, 130, 246, 0.15)', 
                                                                    border: `1px solid ${THEME.primary}`, 
                                                                    color: '#60a5fa', 
                                                                    padding: '2px 10px', 
                                                                    borderRadius: '4px', 
                                                                    fontSize: '0.8rem',
                                                                    fontWeight: 500
                                                                }}>
                                                                    🏢 {name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div style={{display:'flex', gap:'10px', marginTop:'10px'}}>
                                                        {r.photos?.map(p => (
                                                            <a key={p.id} href={p.image} target="_blank" rel="noopener noreferrer">
                                                                <img src={p.image} style={{width:'80px', height:'80px', borderRadius:'4px', objectFit:'cover', border:`1px solid ${THEME.border}`}} />
                                                            </a>
                                                        ))}
                                                    </div>
                                                    
                                                    <LinkedActionsList actions={r.corrective_actions} />
                                                    
                                                    <div style={{marginTop:'20px', paddingTop:'20px', borderTop:`1px dashed ${THEME.border}`, display:'flex', gap:'10px', alignItems:'center'}}>
                                                        <span style={{color:THEME.textSec, fontSize:'0.9rem'}}>Update Status:</span>
                                                        {['OPEN', 'INVESTIGATING', 'CLOSED'].map(s => (
                                                            <button key={s} onClick={() => handleStatus(r.id, s)} style={{
                                                                padding:'5px 12px', 
                                                                borderRadius:'4px', 
                                                                border: r.status === s ? `1px solid ${THEME.success}` : `1px solid ${THEME.border}`, 
                                                                background: r.status === s ? `${THEME.success}22` : 'transparent', 
                                                                color: r.status === s ? THEME.success : THEME.textSec, 
                                                                cursor:'pointer'
                                                            }}>{s}</button>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                <div style={{background: THEME.card, padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`, height:'fit-content'}}>
                                                    <h4 style={{marginTop:0, color:'white'}}>Notes & Updates</h4>
                                                    <div style={{maxHeight:'200px', overflowY:'auto', marginBottom:'15px'}}>
                                                        {r.notes?.length === 0 && <div style={{color:THEME.textSec, fontStyle:'italic', fontSize:'0.9rem'}}>No notes yet.</div>}
                                                        {r.notes?.map(n => (
                                                            <div key={n.id} style={{fontSize:'0.85rem', marginBottom:'8px', paddingBottom:'8px', borderBottom:`1px solid ${THEME.border}44`, color:THEME.textSec}}>
                                                                <strong style={{color:'#e2e8f0'}}>{n.author_name}:</strong> {n.text}
                                                                <div style={{fontSize:'0.7rem', marginTop:'2px', opacity:0.7}}>{new Date(n.created_at).toLocaleString()}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div style={{display:'flex', gap:'5px'}}>
                                                        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Add note..." style={{...inputStyle, marginBottom:0}} />
                                                        <button onClick={() => sendNote(r.id)} style={{background: THEME.primary, color:'white', border:'none', borderRadius:'6px', padding:'0 15px', cursor:'pointer', fontWeight:'bold'}}>Post</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </td></tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// 2. AUDIT HISTORY 
// ==========================================
const AuditHistory = ({ onEdit, isMobile, initialExpandedId }) => {
    const [audits, setAudits] = useState([]);
    const [templates, setTemplates] = useState([]); 
    const [expanded, setExpanded] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCompany, setFilterCompany] = useState('');
    const [filterDate, setFilterDate] = useState('');

    useEffect(() => { 
        Promise.all([api.get('audits/'), api.get('audit-templates/')]).then(([resAudits, resTemplates]) => {
            setAudits(resAudits.data); setTemplates(resTemplates.data);
            if (initialExpandedId) {
                const found = resAudits.data.find(a => a.id === initialExpandedId);
                if (found) {
                    setExpanded(found.id);
                    setTimeout(() => {
                        const el = document.getElementById(`audit-row-${found.id}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 500);
                }
            }
        }); 
    }, [initialExpandedId]);

    const getQuestionText = (audit, qId) => {
        const tmpl = templates.find(t => t.id === audit.template);
        if (!tmpl || !tmpl.sections) return `Question ${qId}`; 
        for (const section of tmpl.sections) {
            const q = section.questions.find(q => String(q.id) === String(qId));
            if (q) return q.text;
        }
        return `Question ${qId}`;
    };

    const getTemplateName = (audit) => {
        const tmpl = templates.find(t => t.id === audit.template);
        return tmpl ? tmpl.name : 'Unknown/Deleted';
    };

    // UPDATED FILTER LOGIC FOR SCOPED ID
    const filtered = useMemo(() => {
        return audits.filter(a => {
            const matchSearch = !searchQuery || 
                (a.display_id || '').toLowerCase().includes(searchQuery.toLowerCase()) || // Check AUD-001
                (a.location || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                (a.observation || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                (a.auditor_name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                a.id.toString().includes(searchQuery);
            
            const matchCompany = !filterCompany || (a.company_name || '').toLowerCase().includes(filterCompany.toLowerCase());
            const matchDate = !filterDate || a.date === filterDate;
            return matchSearch && matchCompany && matchDate;
        });
    }, [audits, searchQuery, filterCompany, filterDate]);

    const handleExport = () => {
        if (filtered.length === 0) return alert("No audits to export");
        const headers = ["ID", "Display ID", "Date", "Template", "Company", "Auditor", "Location", "Action Code", "Summary"];
        const csvRows = [headers.join(',')];
        filtered.forEach(a => {
            const clean = (t) => `"${(t||'').toString().replace(/"/g, '""')}"`;
            csvRows.push([
                a.id, 
                clean(a.display_id), // Export Scoped ID
                a.date, clean(getTemplateName(a)), 
                clean(a.company_name), clean(a.auditor_name), 
                clean(a.location), a.action_code, clean(a.observation)
            ].join(','));
        });
        const url = window.URL.createObjectURL(new Blob([csvRows.join('\n')], { type: 'text/csv' }));
        const link = document.createElement('a'); link.href = url; link.setAttribute('download', `Safety_Audits_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link); link.click(); link.remove();
    };

    const handlePrint = async (audit) => {
        let templateData = templates.find(t => t.id === audit.template);
        const w = window.open('', '', 'height=800,width=800');
        w.document.write('<html><head><title>Audit Report</title><style>body{font-family: sans-serif; padding: 40px; color:#333} .label{font-weight:bold; color:#666} .q-row{border-bottom:1px solid #eee; padding:5px 0; display:flex; justify-content:space-between} .photo-grid{display:grid; grid-template-columns:repeat(3, 1fr); gap:10px; margin-top:10px} img{width:100%; height:150px; object-fit:cover}</style></head><body>');
        // UPDATED: Show Display ID in Print
        w.document.write(`<h1>Audit ${audit.display_id || '#'+audit.id}</h1><p><span class="label">Date:</span> ${audit.date}</p><p><span class="label">Template:</span> ${getTemplateName(audit)}</p><p><span class="label">Company:</span> ${audit.company_name}</p><p><span class="label">Location:</span> ${audit.location}</p>`);
        if(audit.observation_types && audit.observation_types.length > 0) w.document.write(`<p><span class="label">Observation Types:</span> ${audit.observation_types.join(', ')}</p>`);
        if(audit.action_code) w.document.write(`<p><span class="label">Action Code:</span> ${audit.action_code}</p>`);
        w.document.write('<hr/><h3>Checklist</h3>');
        if (templateData && templateData.sections) {
            templateData.sections.forEach(sec => {
                w.document.write(`<h4>${sec.title}</h4>`);
                sec.questions.forEach(q => {
                    const ans = audit.checklist_responses?.[String(q.id)] || '-';
                    w.document.write(`<div class="q-row"><span>${q.text}</span><b>${ans}</b></div>`);
                });
            });
        } else if(audit.checklist_responses) {
            Object.entries(audit.checklist_responses).forEach(([k, v]) => w.document.write(`<div>Q${k}: ${v}</div>`));
        }
        w.document.write(`<hr/><h3>Summary</h3><p>${audit.observation}</p>`);
        if(audit.photos && audit.photos.length > 0) {
            w.document.write('<h4>Evidence</h4><div class="photo-grid">');
            audit.photos.forEach(p => w.document.write(`<div><img src="${p.image}"/></div>`));
            w.document.write('</div>');
        }
        w.document.write('</body></html>');
        w.document.close();
        setTimeout(() => w.print(), 500);
    };

    return (
        <div className="card" style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden'}}>
            <div style={{padding:'15px', borderBottom:`1px solid ${THEME.border}`, background: THEME.card}}>
                <div style={{display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center'}}>
                    <input placeholder="🔍 Search Audits..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{...inputStyle, flex: 2, minWidth:'200px', marginBottom:0}} />
                    <input placeholder="🏢 Company..." value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={{...inputStyle, flex: 1, minWidth:'150px', marginBottom:0}} />
                    <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={{...inputStyle, width:'auto', marginBottom:0}} />
                    <button onClick={handleExport} style={{padding:'8px 16px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>📥 Excel</button>
                    <button onClick={() => { setSearchQuery(''); setFilterCompany(''); setFilterDate(''); }} style={{padding:'8px 12px', background:'transparent', border:`1px solid ${THEME.textSec}`, color:THEME.textSec, borderRadius:'6px', cursor:'pointer'}}>Clear</button>
                </div>
            </div>
            <div style={{overflowX: 'auto'}}>
                <table style={{width:'100%', minWidth: '700px', borderCollapse:'collapse', textAlign:'left', color: THEME.textSec, fontSize:'0.9rem'}}>
                    <thead style={{background:'#020617', borderBottom:`1px solid ${THEME.border}`}}>
                        <tr><th style={{padding:'15px'}}>ID</th><th>Date</th><th>Template</th><th>Company</th><th>Auditor</th><th>Action Code</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                        {filtered.map(a => (
                            <React.Fragment key={a.id}>
                                <tr id={`audit-row-${a.id}`} onClick={() => setExpanded(expanded === a.id ? null : a.id)} style={{borderBottom:`1px solid ${THEME.border}`, cursor:'pointer', background: expanded === a.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent'}}>
                                    {/* UPDATED: Show Display ID */}
                                    <td style={{padding:'15px', color:THEME.textSec}}>{a.display_id || '#'+a.id}</td>
                                    <td style={{color:'white'}}>{a.date}</td>
                                    <td style={{color:THEME.primary}}>{getTemplateName(a)}</td>
                                    <td style={{color:THEME.warning, fontWeight:'bold'}}>{a.company_name}</td>
                                    <td>{a.auditor_name}</td>
                                    <td><span style={{padding:'2px 8px', borderRadius:'4px', background: a.action_code ? THEME.danger : THEME.success, color:'white', fontSize:'0.75rem'}}>{a.action_code || 'None'}</span></td>
                                    <td style={{padding:'15px', display:'flex', gap:'10px'}}>
                                        <button onClick={(e) => { e.stopPropagation(); onEdit(a); }} style={{background:'transparent', border:`1px solid ${THEME.primary}`, color:THEME.primary, padding:'4px 10px', borderRadius:'4px', cursor:'pointer'}}>Edit</button>
                                        <button onClick={(e) => { e.stopPropagation(); handlePrint(a); }} style={{background:'transparent', border:`1px solid ${THEME.textSec}`, color:'white', padding:'4px 10px', borderRadius:'4px', cursor:'pointer'}}>🖨️ PDF</button>
                                    </td>
                                </tr>
                                {expanded === a.id && (
                                    <tr style={{background:'#020617', borderBottom:`2px solid ${THEME.primary}`}}><td colSpan="7" style={{padding:'20px'}}>
                                        <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'20px'}}>
                                            <div>
                                                <h4 style={{marginTop:0, color:'white'}}>Summary</h4>
                                                <p>{a.observation}</p>
                                                {a.observation_types && a.observation_types.length > 0 && <div style={{marginBottom:'10px'}}><strong>Types: </strong>{a.observation_types.map(t => <span key={t} style={{marginRight:'5px', padding:'2px 6px', background:'rgba(59,130,246,0.2)', borderRadius:'4px', fontSize:'0.8rem', color:THEME.primary}}>{t}</span>)}</div>}
                                                {a.photos?.length > 0 && <div style={{display:'flex', gap:'10px'}}>{a.photos.map(p => <a key={p.id} href={p.image} target="_blank"><img src={p.image} style={{width:'80px', height:'80px', borderRadius:'4px'}} /></a>)}</div>}
                                                
                                                {/* --- LINKED ACTIONS --- */}
                                                <LinkedActionsList actions={a.corrective_actions} />
                                            </div>
                                            <div>
                                                <h4 style={{marginTop:0, color:'white'}}>Checklist</h4>
                                                {Object.entries(a.checklist_responses || {}).map(([k,v]) => (
                                                    <div key={k} style={{display:'flex', justifyContent:'space-between', borderBottom:'1px dashed #334155', padding:'4px 0'}}>
                                                        <span style={{color:'#cbd5e1', paddingRight:'15px', flex:1}}>{getQuestionText(a, k)}</span> 
                                                        <strong style={{color: v==='No'||v==='Fail' ? THEME.danger : THEME.success}}>{v}</strong>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </td></tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ... (AuditBuilder, TemplateEditor, AuditForm, FileReportForm remain the same, as they don't display list IDs) ...
// Ensure you include them below just like in your original code to keep the file complete.

// ==========================================
// 3. AUDIT BUILDER 
// ==========================================
const AuditBuilder = ({ isMobile }) => {
    const [templates, setTemplates] = useState([]);
    const [editing, setEditing] = useState(null);
    useEffect(() => { load(); }, []);
    const load = () => api.get('audit-templates/').then(res => setTemplates(res.data));
    const handleCreate = async () => { const name = prompt("Template Name:"); if (name) { await api.post('audit-templates/', { name, observation_types: ["Good", "Risk"], action_codes: ["Fix", "Monitor"] }); load(); } };
    if (editing) return <TemplateEditor template={editing} onBack={() => { setEditing(null); load(); }} isMobile={isMobile} />;
    return (
        <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap:'20px'}}>
            <div onClick={handleCreate} style={{border:`2px dashed ${THEME.border}`, borderRadius:'12px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', minHeight:'150px', color:THEME.textSec, flexDirection:'column'}}><span style={{fontSize:'2rem'}}>+</span><span>Create New Template</span></div>
            {templates.map(t => (<div key={t.id} style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`}}><h3 style={{marginTop:0, color:'white'}}>{t.name}</h3><p style={{color:THEME.textSec}}>{t.sections?.length || 0} Sections</p><div style={{display:'flex', gap:'10px', marginTop:'20px'}}><button onClick={() => setEditing(t)} style={{flex:1, padding:'8px', background:'transparent', border:`1px solid ${THEME.primary}`, color:THEME.primary, borderRadius:'6px', cursor:'pointer'}}>Edit</button><button onClick={async () => { if(window.confirm('Delete?')) { await api.delete(`audit-templates/${t.id}/`); load(); }}} style={{padding:'8px', background:'transparent', border:`1px solid ${THEME.danger}`, color:THEME.danger, borderRadius:'6px', cursor:'pointer'}}>🗑️</button></div></div>))}
        </div>
    );
};

const TemplateEditor = ({ template, onBack, isMobile }) => {
    const [sections, setSections] = useState(template.sections || []);
    const [obsTypes, setObsTypes] = useState(template.observation_types.join('\n'));
    const [actCodes, setActCodes] = useState(template.action_codes.join('\n'));
    
    const save = async () => {
        await api.patch(`audit-templates/${template.id}/`, {
            observation_types: obsTypes.split('\n').filter(s => s.trim()),
            action_codes: actCodes.split('\n').filter(s => s.trim())
        });
        await api.post(`audit-templates/${template.id}/update_structure/`, { sections });
        alert("Saved");
    };

    const addSection = () => setSections([...sections, { title: 'New Section', questions: [] }]);
    const addQ = (sIdx) => { const n = [...sections]; n[sIdx].questions.push({ text: '', question_type: 'YES_NO' }); setSections(n); };
    
    return (
        <div>
            <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px'}}><button onClick={onBack} style={{background:'transparent', border:`1px solid ${THEME.border}`, color:THEME.textSec, padding:'8px 16px', borderRadius:'6px', cursor:'pointer'}}>← Back</button><button onClick={save} style={{background: THEME.success, color:'white', border:'none', padding:'8px 20px', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Save Changes</button></div>
            <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap:'20px'}}>
                <div className="card" style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`, height:'fit-content'}}><h4 style={{marginTop:0, color:THEME.primary}}>⚙️ Settings</h4><label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem'}}>Observation Types (1 per line)</label><textarea rows="5" value={obsTypes} onChange={e => setObsTypes(e.target.value)} style={inputStyle} /><label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginTop:'10px'}}>Action Codes (1 per line)</label><textarea rows="5" value={actCodes} onChange={e => setActCodes(e.target.value)} style={inputStyle} /></div>
                <div style={{display:'grid', gap:'20px'}}>{sections.map((s, sIdx) => (<div key={sIdx} style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`}}><div style={{display:'flex', gap:'10px', marginBottom:'15px'}}><input value={s.title} onChange={e => { const n = [...sections]; n[sIdx].title = e.target.value; setSections(n); }} style={{...inputStyle, fontSize:'1.1rem', fontWeight:'bold', marginBottom:0}} placeholder="Section Title" /><button onClick={() => { const n = [...sections]; n.splice(sIdx, 1); setSections(n); }} style={{background: THEME.danger, border:'none', color:'white', borderRadius:'6px', width:'40px', cursor:'pointer'}}>✕</button></div>{s.questions.map((q, qIdx) => (<div key={qIdx} style={{display:'flex', gap:'10px', marginBottom:'10px'}}><input value={q.text} onChange={e => { const n = [...sections]; n[sIdx].questions[qIdx].text = e.target.value; setSections(n); }} style={{...inputStyle, marginBottom:0}} placeholder="Question..." /><select value={q.question_type} onChange={e => { const n = [...sections]; n[sIdx].questions[qIdx].question_type = e.target.value; setSections(n); }} style={{...inputStyle, width:'150px', marginBottom:0}}><option value="YES_NO">Yes/No</option><option value="TEXT">Text</option><option value="CHECKBOX">Checkbox</option></select><button onClick={() => { const n = [...sections]; n[sIdx].questions.splice(qIdx, 1); setSections(n); }} style={{background: 'transparent', border:'none', color:THEME.danger, cursor:'pointer'}}>✕</button></div>))}<button onClick={() => addQ(sIdx)} style={{background:'transparent', border:`1px dashed ${THEME.primary}`, color:THEME.primary, width:'100%', padding:'8px', borderRadius:'6px', cursor:'pointer'}}>+ Add Question</button></div>))}<button onClick={addSection} style={{padding:'15px', background: THEME.bg, border:`2px dashed ${THEME.border}`, color:THEME.textSec, borderRadius:'12px', cursor:'pointer'}}>+ Add Section</button></div>
            </div>
        </div>
    );
};

// ==========================================
// 4. AUDIT FORM 
// ==========================================
const AuditForm = ({ initialData, onSuccess, onCancel, isMobile }) => {
    const [companies, setCompanies] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formData, setFormData] = useState({ company: '', location: '', observation: '', observation_types: [], action_code: '', action_required: '', checklist_responses: {} });
    
    // UPDATED: Stores array of File objects
    const [photos, setPhotos] = useState([]); 
    const fileInputRef = useRef(null);

    useEffect(() => {
        Promise.all([api.get('companies/'), api.get('audit-templates/')]).then(([c, t]) => {
            setCompanies(c.data); setTemplates(t.data);
            if (initialData) {
                setFormData({ ...initialData, checklist_responses: initialData.checklist_responses || {}, observation_types: initialData.observation_types || [] });
                if (initialData.template) {
                    const tmpl = t.data.find(x => x.id === initialData.template);
                    setSelectedTemplate(tmpl);
                }
            }
        });
    }, [initialData]);

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            setPhotos(prev => [...prev, ...newFiles]);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removePhoto = (index) => {
        setPhotos(prev => prev.filter((_, i) => i !== index));
    };

    const submit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        Object.keys(formData).forEach(k => {
            if(k==='checklist_responses'||k==='observation_types') data.append(k, JSON.stringify(formData[k]));
            else data.append(k, formData[k]);
        });
        if (selectedTemplate) data.append('template', selectedTemplate.id);
        photos.forEach(p => data.append('photos', p));
        try {
            if (initialData) await api.put(`audits/${initialData.id}/`, data);
            else await api.post('audits/', data);
            alert("✅ Audit Saved"); onSuccess();
        } catch (err) { alert("Error saving: " + JSON.stringify(err.response?.data)); }
    };

    const handleChecklistChange = (qId, val) => {
        setFormData(prev => ({ ...prev, checklist_responses: { ...prev.checklist_responses, [qId]: val } }));
    };

    return (
        <div className="card" style={{maxWidth:'800px', margin:'0 auto', background: THEME.card, padding: isMobile ? '15px' : '30px', borderRadius:'12px', border:`1px solid ${THEME.primary}`}}>
            <h3 style={{marginTop:0, color:'white'}}>{initialData ? 'Edit Audit' : 'Conduct New Audit'}</h3>
            <form onSubmit={submit}>
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'20px'}}>
                    <div><label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Template</label><select onChange={e => setSelectedTemplate(templates.find(t => t.id === parseInt(e.target.value)))} value={selectedTemplate?.id || ''} disabled={!!initialData} style={inputStyle}><option value="">-- Select --</option>{templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
                    <div><label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Company</label><select value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} style={inputStyle}><option value="">-- Select --</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                </div>
                <label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Location</label><input value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} style={inputStyle} />
                
                {selectedTemplate && selectedTemplate.observation_types && selectedTemplate.observation_types.length > 0 && (
                    <div style={{marginBottom:'20px'}}>
                         <label style={{color:THEME.textSec, fontSize:'0.8rem', display:'block', marginBottom:'8px'}}>Observation Type</label>
                         <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>
                            {selectedTemplate.observation_types.map(type => (
                                <label key={type} style={{padding:'6px 12px', borderRadius:'20px', border:`1px solid ${formData.observation_types.includes(type) ? THEME.primary : THEME.border}`, background: formData.observation_types.includes(type) ? 'rgba(59, 130, 246, 0.2)' : 'transparent', color: formData.observation_types.includes(type) ? 'white' : THEME.textSec, cursor:'pointer', fontSize:'0.85rem'}}>
                                    <input type="checkbox" style={{display:'none'}} checked={formData.observation_types.includes(type)} onChange={() => { const current = formData.observation_types; const updated = current.includes(type) ? current.filter(t => t !== type) : [...current, type]; setFormData({...formData, observation_types: updated}); }} />
                                    {type}
                                </label>
                            ))}
                         </div>
                    </div>
                )}

                {selectedTemplate && selectedTemplate.action_codes && selectedTemplate.action_codes.length > 0 && (
                    <div>
                        <label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Action Code</label>
                        <select value={formData.action_code} onChange={e => setFormData({...formData, action_code: e.target.value})} style={inputStyle}>
                            <option value="">-- Select Action Code --</option>
                            {selectedTemplate.action_codes.map(ac => <option key={ac} value={ac}>{ac}</option>)}
                        </select>
                    </div>
                )}

                {selectedTemplate && selectedTemplate.sections && (
                    <div style={{background:'#020617', padding:'20px', borderRadius:'8px', border:`1px solid ${THEME.border}`, marginBottom:'20px'}}>
                        <h4 style={{marginTop:0, color:THEME.primary}}>📋 Checklist</h4>
                        {selectedTemplate.sections.map(sec => (
                            <div key={sec.id} style={{marginBottom:'20px'}}>
                                <h5 style={{color:'white', borderBottom:`1px solid ${THEME.border}`, paddingBottom:'5px', marginBottom:'10px'}}>{sec.title}</h5>
                                {sec.questions.map(q => {
                                    if (q.showIf) { const parentAns = formData.checklist_responses[q.showIf]; if (!parentAns || parentAns === 'No') return null; }
                                    return (
                                        <div key={q.id} style={{marginBottom:'12px', borderBottom:'1px dashed #334155', paddingBottom:'12px', paddingLeft: q.showIf ? '15px' : 0, borderLeft: q.showIf ? `2px solid ${THEME.border}` : 'none'}}>
                                            {q.question_type === 'TEXT' ? (
                                                <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                                                    <label style={{color:'#cbd5e1', fontSize:'0.9rem'}}>{q.text}</label>
                                                    <textarea rows="2" value={formData.checklist_responses[q.id]||''} onChange={e=>handleChecklistChange(q.id,e.target.value)} style={{width:'100%', background:'#1e293b', color:'white', border:'1px solid #475569', borderRadius:'4px', padding:'8px', boxSizing:'border-box', resize:'vertical'}} />
                                                </div>
                                            ) : q.question_type === 'CHECKBOX' ? (
                                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                                    <label style={{color:'#cbd5e1', fontSize:'0.9rem', paddingRight:'15px', flex:1}}>{q.text}</label>
                                                    <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
                                                        <span style={{fontSize:'0.8rem', color: formData.checklist_responses[q.id]==='Checked' ? THEME.success : THEME.textSec}}>{formData.checklist_responses[q.id]==='Checked' ? 'Pass' : 'Fail'}</span>
                                                        <input type="checkbox" checked={formData.checklist_responses[q.id]==='Checked'} onChange={e=>handleChecklistChange(q.id,e.target.checked?'Checked':'')} style={{width:'20px', height:'20px', cursor:'pointer', accentColor: THEME.success}} />
                                                    </label>
                                                </div>
                                            ) : (
                                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                                    <label style={{color:'#cbd5e1', fontSize:'0.9rem', paddingRight:'15px', flex:1}}>{q.text}</label>
                                                    <select value={formData.checklist_responses[q.id]||''} onChange={e=>handleChecklistChange(q.id,e.target.value)} style={{width:'100px', padding:'6px', background:'#1e293b', color:'white', border:'1px solid #475569', borderRadius:'4px'}}><option value="">-</option><option>Yes</option><option>No</option><option>NA</option></select>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}

                <label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Observations</label><textarea rows="3" value={formData.observation} onChange={e => setFormData({...formData, observation: e.target.value})} style={inputStyle} />
                
                <label style={{color:THEME.textSec, fontSize:'0.8rem', display:'block', marginBottom:'5px'}}>Evidence Photos</label>
                <div style={{marginBottom:'10px', display:'flex', gap:'10px', flexWrap:'wrap'}}>
                    {photos.map((file, idx) => (
                        <div key={idx} style={{display:'flex', alignItems:'center', gap:'8px', background:'rgba(59, 130, 246, 0.2)', padding:'6px 12px', borderRadius:'6px', fontSize:'0.85rem', color:'white', border:`1px solid ${THEME.primary}`}}>
                            <span>{file.name}</span>
                            <button type="button" onClick={() => removePhoto(idx)} style={{background:'transparent', border:'none', color:THEME.danger, cursor:'pointer', fontWeight:'bold', fontSize:'1rem'}}>×</button>
                        </div>
                    ))}
                </div>
                
                <div style={{...inputStyle, padding:'6px', display:'flex', alignItems:'center', gap:'10px'}}>
                    <button type="button" onClick={() => fileInputRef.current.click()} style={{background:'#1e293b', border:`1px solid ${THEME.border}`, color:THEME.textSec, padding:'6px 12px', borderRadius:'4px', cursor:'pointer'}}>📷 Add Photos</button>
                    <input 
                        ref={fileInputRef}
                        type="file" 
                        multiple 
                        accept="image/*"
                        onChange={handleFileSelect} 
                        style={{display:'none'}} 
                    />
                    <span style={{fontSize:'0.85rem', color:THEME.textSec}}>{photos.length === 0 ? 'No photos selected' : `${photos.length} photos ready`}</span>
                </div>

                <div style={{display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'20px'}}>
                    <button type="button" onClick={onCancel} style={{padding:'10px 20px', background:'transparent', border:`1px solid ${THEME.border}`, color:THEME.textSec, borderRadius:'6px', cursor:'pointer'}}>Cancel</button>
                    <button type="submit" style={{padding:'10px 20px', background: THEME.primary, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Save Audit</button>
                </div>
            </form>
        </div>
    );
};

// ==========================================
// 5. FILE REPORT FORM
// ==========================================
const FileReportForm = ({ onSuccess, isMobile }) => {
    const [newReport, setNewReport] = useState({ report_type: 'HAZARD', description: '', location: '', severity: 'LOW', companies_involved: [] });
    
    // UPDATED: Stores array of File objects
    const [selectedFiles, setSelectedFiles] = useState([]); 
    const fileInputRef = useRef(null);

    const [availableCompanies, setAvailableCompanies] = useState([]);
    
    useEffect(() => { api.get('companies/').then(res => setAvailableCompanies(res.data)).catch(console.error); }, []);
    
    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            setSelectedFiles(prev => [...prev, ...newFiles]);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removePhoto = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => { 
        e.preventDefault(); 
        const formData = new FormData(); 
        Object.keys(newReport).forEach(k => { 
            if(k==='companies_involved') newReport[k].forEach(id=>formData.append('companies_involved',id)); 
            else formData.append(k, newReport[k]); 
        }); 
        selectedFiles.forEach(f => formData.append('photos', f)); 
        try { await api.post('safety/', formData); alert("✅ Report Submitted"); if (onSuccess) onSuccess(); } catch (err) { alert("Error"); } 
    };
    
    return (
        <div className="card" style={{maxWidth:'800px', margin:'0 auto', background: THEME.card, padding: isMobile ? '15px' : '30px', borderRadius:'12px', border:`1px solid ${THEME.danger}`}}>
            <h3 style={{marginTop:0, color:'white'}}>🚨 File Report</h3>
            <form onSubmit={handleSubmit}>
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'20px'}}>
                    <div><label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Type</label><select value={newReport.report_type} onChange={e => setNewReport({...newReport, report_type: e.target.value})} style={inputStyle}><option value="HAZARD">Hazard</option><option value="INCIDENT">Incident</option></select></div>
                    <div><label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Severity</label><select value={newReport.severity} onChange={e => setNewReport({...newReport, severity: e.target.value})} style={inputStyle}><option>LOW</option><option>HIGH</option><option>CRITICAL</option></select></div>
                </div>
                <label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Location</label><input value={newReport.location} onChange={e => setNewReport({...newReport, location: e.target.value})} style={inputStyle} required />
                <label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Companies Involved</label>
                <div style={{display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'15px'}}>
                    {availableCompanies.map(c => <label key={c.id} style={{color:'white', background: newReport.companies_involved.includes(c.id)?THEME.danger:'transparent', padding:'4px 8px', borderRadius:'4px', border:`1px solid ${THEME.border}`}}><input type="checkbox" onChange={() => setNewReport(p => ({...p, companies_involved: p.companies_involved.includes(c.id) ? p.companies_involved.filter(x=>x!==c.id) : [...p.companies_involved, c.id]}))} style={{display:'none'}} /> {c.name}</label>)}
                </div>
                <label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Description</label><textarea rows="4" value={newReport.description} onChange={e => setNewReport({...newReport, description: e.target.value})} style={inputStyle} required />
                
                {/* --- UPDATED PHOTOS SECTION WITH PILLS --- */}
                <label style={{color:THEME.textSec, fontSize:'0.8rem', display:'block', marginBottom:'5px'}}>Photos</label>
                <div style={{marginBottom:'10px', display:'flex', gap:'10px', flexWrap:'wrap'}}>
                    {selectedFiles.map((file, idx) => (
                        <div key={idx} style={{display:'flex', alignItems:'center', gap:'8px', background:'rgba(239, 68, 68, 0.2)', padding:'6px 12px', borderRadius:'6px', fontSize:'0.85rem', color:'white', border:`1px solid ${THEME.danger}`}}>
                            <span>{file.name}</span>
                            <button type="button" onClick={() => removePhoto(idx)} style={{background:'transparent', border:'none', color:THEME.danger, cursor:'pointer', fontWeight:'bold', fontSize:'1rem'}}>×</button>
                        </div>
                    ))}
                </div>

                <div style={{...inputStyle, padding:'6px', display:'flex', alignItems:'center', gap:'10px'}}>
                    <button type="button" onClick={() => fileInputRef.current.click()} style={{background:'#1e293b', border:`1px solid ${THEME.border}`, color:THEME.textSec, padding:'6px 12px', borderRadius:'4px', cursor:'pointer'}}>📷 Add Photos</button>
                    <input 
                        ref={fileInputRef}
                        type="file" 
                        multiple 
                        accept="image/*"
                        onChange={handleFileSelect} 
                        style={{display:'none'}} 
                    />
                    <span style={{fontSize:'0.85rem', color:THEME.textSec}}>{selectedFiles.length === 0 ? 'No photos selected' : `${selectedFiles.length} photos ready`}</span>
                </div>
                
                <button type="submit" style={{marginTop:'20px', width:'100%', padding:'12px', background:THEME.danger, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold'}}>Submit Report</button>
            </form>
        </div>
    );
};

export default SafetyManager;